import { apiClient } from './client'
import type {
  SubjectSuggestion,
  BackgroundSuggestion,
  QualificationMatrixStatus,
  TokenCheckResponse,
  SubjectAliasResponse,
  BackgroundAliasResponse,
  SubjectWithBackgroundResponse,
  BackgroundResponse,
  PdfPageResponse,
  ImportSessionUndoResponse,
} from '@/types/tokenResolution'

const BASE_PATH = '/api/v1/timetables/v2'

export const tokenResolutionApi = {
  getSubjectSuggestions: async (token: string): Promise<SubjectSuggestion[]> => {
    const response = await apiClient.get(`${BASE_PATH}/subjects/suggestions`, {
      params: { token },
    })
    return response.data
  },

  getBackgroundSuggestions: async (token: string): Promise<BackgroundSuggestion[]> => {
    const response = await apiClient.get(`${BASE_PATH}/backgrounds/suggestions`, {
      params: { token },
    })
    return response.data
  },

  getQualificationMatrixStatus: async (): Promise<QualificationMatrixStatus> => {
    const response = await apiClient.get(`${BASE_PATH}/qualification-matrix/status`)
    return response.data
  },

  checkTokenExists: async (token: string): Promise<TokenCheckResponse> => {
    const response = await apiClient.get(`${BASE_PATH}/token/check`, {
      params: { token },
    })
    return response.data
  },

  createSubjectAlias: async (data: {
    alias_name: string
    canonical_subject_id: string
  }): Promise<SubjectAliasResponse> => {
    const response = await apiClient.post(`${BASE_PATH}/subjects/aliases`, data)
    return response.data
  },

  createBackgroundAlias: async (data: {
    alias_name: string
    canonical_background_id: string
  }): Promise<BackgroundAliasResponse> => {
    const response = await apiClient.post(`${BASE_PATH}/backgrounds/aliases`, data)
    return response.data
  },

  createSubjectWithBackground: async (data: {
    subject_name: string
    subject_code?: string
    background_id?: string
    background_ids?: string[]
    create_new_background?: boolean
    new_background_name?: string
    new_background_code?: string
    qualification_type?: 'qualified' | 'group'
    group_name?: string
  }, importSessionId?: string): Promise<SubjectWithBackgroundResponse> => {
    const params = importSessionId ? { import_session_id: importSessionId } : {}
    const response = await apiClient.post(`${BASE_PATH}/subjects/with-background`, data, { params })
    return response.data
  },

  createBackground: async (data: {
    name: string
    code?: string
    description?: string
  }, importSessionId?: string): Promise<BackgroundResponse> => {
    const params = importSessionId ? { import_session_id: importSessionId } : {}
    const response = await apiClient.post(`${BASE_PATH}/backgrounds`, data, { params })
    return response.data
  },

  getSessionPdfPage: async (sessionId: string, pageNumber?: number, token?: string): Promise<PdfPageResponse> => {
    const params: Record<string, string | number> = {}
    if (pageNumber) params.page_number = pageNumber
    if (token) params.token = token
    console.log('[tokenResolutionApi] Fetching PDF page for session:', sessionId, 'params:', params)
    const url = `${BASE_PATH}/session/${sessionId}/pdf-page`
    console.log('[tokenResolutionApi] URL:', url)
    try {
      const response = await apiClient.get(url, { params })
      console.log('[tokenResolutionApi] PDF response:', response.data ? 'received' : 'empty')
      return response.data
    } catch (err) {
      console.error('[tokenResolutionApi] PDF fetch error:', err)
      throw err
    }
  },

  undoImportSession: async (sessionId: string): Promise<ImportSessionUndoResponse> => {
    const response = await apiClient.post(`${BASE_PATH}/import-session/undo`, {
      session_id: sessionId,
    })
    return response.data
  },

  deleteSubjectAlias: async (aliasId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/subjects/aliases/${aliasId}`)
  },

  deleteBackgroundAlias: async (aliasId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/backgrounds/aliases/${aliasId}`)
  },
}